import React, { useState, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
    Users, FolderKanban, CheckCircle2, Clock, AlertTriangle,
    TrendingUp, BarChart3, Target, ArrowUpRight, ArrowDownRight,
    Filter, Search, ChevronDown, Eye
} from 'lucide-react';
import { Issue, User, Project } from '../../types';
import { UserAvatar } from '../UI/UserAvatar';

interface ManagerDashboardProps {
    issues: Issue[];
    users: User[];
    projects: Project[];
    currentUser: User | null;
    onIssueClick?: (issue: Issue) => void;
    onProjectSelect?: (project: Project) => void;
}

export const ManagerDashboard: React.FC<ManagerDashboardProps> = ({
    issues,
    users,
    projects,
    currentUser,
    onIssueClick,
    onProjectSelect,
}) => {
    const [selectedProject, setSelectedProject] = useState<string>('all');
    const [selectedUser, setSelectedUser] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'projects' | 'tasks'>('overview');
    const [taskSortField, setTaskSortField] = useState<'key' | 'summary' | 'status' | 'priority' | 'dueDate'>('key');
    const [taskSortDir, setTaskSortDir] = useState<'asc' | 'desc'>('asc');
    const [taskPage, setTaskPage] = useState(0);
    const TASKS_PER_PAGE = 25;

    // Filtered issues
    const filteredIssues = useMemo(() => {
        let result = [...issues];
        if (selectedProject !== 'all') {
            result = result.filter(i => i.projectId === selectedProject);
        }
        if (selectedUser !== 'all') {
            result = result.filter(i =>
                i.assignee === selectedUser ||
                i.assignees?.includes(selectedUser) ||
                i.reporter === selectedUser
            );
        }
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(i =>
                i.summary.toLowerCase().includes(q) ||
                i.key.toLowerCase().includes(q)
            );
        }
        return result;
    }, [issues, selectedProject, selectedUser, searchQuery]);

    // Overall stats
    const overallStats = useMemo(() => {
        const total = filteredIssues.length;
        const done = filteredIssues.filter(i => i.status === 'done').length;
        const inProgress = filteredIssues.filter(i => i.status === 'in-progress').length;
        const todo = filteredIssues.filter(i => ['to-do', 'backlog'].includes(i.status)).length;
        const overdue = filteredIssues.filter(i =>
            i.dueDate && new Date(i.dueDate) < new Date() && i.status !== 'done'
        ).length;
        const inReview = filteredIssues.filter(i => i.status === 'in-review').length;
        const onHold = filteredIssues.filter(i => (i.status as string) === 'on-hold').length;
        const completionRate = total > 0 ? Math.round((done / total) * 100) : 0;

        return { total, done, inProgress, todo, overdue, inReview, onHold, completionRate };
    }, [filteredIssues]);

    // Per-user stats
    const userStats = useMemo(() => {
        return users.map(user => {
            const assigned = filteredIssues.filter(i =>
                i.assignee === user.id || i.assignees?.includes(user.id)
            );
            const reported = filteredIssues.filter(i => i.reporter === user.id);
            const completed = assigned.filter(i => i.status === 'done').length;
            const inProgress = assigned.filter(i => i.status === 'in-progress').length;
            const overdue = assigned.filter(i =>
                i.dueDate && new Date(i.dueDate) < new Date() && i.status !== 'done'
            ).length;
            const totalPoints = assigned.reduce((sum, i) => sum + (i.storyPoints || 0), 0);
            const completedPoints = assigned.filter(i => i.status === 'done').reduce((sum, i) => sum + (i.storyPoints || 0), 0);

            return {
                user,
                assigned: assigned.length,
                reported: reported.length,
                completed,
                inProgress,
                overdue,
                totalPoints,
                completedPoints,
                completionRate: assigned.length > 0 ? Math.round((completed / assigned.length) * 100) : 0,
            };
        }).sort((a, b) => b.assigned - a.assigned);
    }, [users, filteredIssues]);

    // Per-project stats
    const projectStats = useMemo(() => {
        return projects.map(project => {
            const projectIssues = filteredIssues.filter(i => i.projectId === project.id);
            const total = projectIssues.length;
            const done = projectIssues.filter(i => i.status === 'done').length;
            const inProgress = projectIssues.filter(i => i.status === 'in-progress').length;
            const overdue = projectIssues.filter(i =>
                i.dueDate && new Date(i.dueDate) < new Date() && i.status !== 'done'
            ).length;
            const totalPoints = projectIssues.reduce((sum, i) => sum + (i.storyPoints || 0), 0);
            const completedPoints = projectIssues.filter(i => i.status === 'done').reduce((sum, i) => sum + (i.storyPoints || 0), 0);

            // Unique assignees
            const assigneeIds = new Set<string>();
            projectIssues.forEach(i => {
                if (i.assignee) assigneeIds.add(i.assignee);
                i.assignees?.forEach(a => assigneeIds.add(a));
            });

            return {
                project,
                total,
                done,
                inProgress,
                overdue,
                totalPoints,
                completedPoints,
                assigneeCount: assigneeIds.size,
                completionRate: total > 0 ? Math.round((done / total) * 100) : 0,
            };
        }).sort((a, b) => b.total - a.total);
    }, [projects, filteredIssues]);

    // Priority breakdown
    const priorityBreakdown = useMemo(() => {
        const counts: Record<string, number> = { highest: 0, high: 0, medium: 0, low: 0, lowest: 0 };
        filteredIssues.forEach(i => {
            if (counts[i.priority] !== undefined) counts[i.priority]++;
        });
        return counts;
    }, [filteredIssues]);

    // Type breakdown
    const typeBreakdown = useMemo(() => {
        const counts: Record<string, number> = { bug: 0, story: 0, task: 0, epic: 0 };
        filteredIssues.forEach(i => {
            if (counts[i.type] !== undefined) counts[i.type]++;
        });
        return counts;
    }, [filteredIssues]);

    // Monthly trend data (last 12 months)
    const monthlyTrend = useMemo(() => {
        const now = new Date();
        const months: { label: string; completed: number; pending: number; created: number }[] = [];
        for (let i = 11; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
            const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
            let completed = 0, pending = 0, created = 0;
            filteredIssues.forEach(issue => {
                const createdAt = new Date(issue.createdAt);
                const updatedAt = new Date(issue.updatedAt);
                if (createdAt.getFullYear() === d.getFullYear() && createdAt.getMonth() === d.getMonth()) {
                    created++;
                }
                if (issue.status === 'done' && updatedAt.getFullYear() === d.getFullYear() && updatedAt.getMonth() === d.getMonth()) {
                    completed++;
                }
                if (issue.status !== 'done' && createdAt <= monthEnd) {
                    if (createdAt.getFullYear() === d.getFullYear() && createdAt.getMonth() <= d.getMonth() ||
                        createdAt.getFullYear() < d.getFullYear()) {
                        pending++;
                    }
                }
            });
            months.push({ label, completed, pending, created });
        }
        return months;
    }, [filteredIssues]);

    // Weekly trend data (last 12 weeks)
    const weeklyTrend = useMemo(() => {
        const now = new Date();
        const weeks: { label: string; completed: number; pending: number; created: number }[] = [];
        for (let i = 11; i >= 0; i--) {
            const weekEnd = new Date(now);
            weekEnd.setDate(weekEnd.getDate() - (i * 7));
            weekEnd.setHours(23, 59, 59, 999);
            const weekStart = new Date(weekEnd);
            weekStart.setDate(weekStart.getDate() - 6);
            weekStart.setHours(0, 0, 0, 0);
            const label = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            let completed = 0, pending = 0, created = 0;
            filteredIssues.forEach(issue => {
                const createdAt = new Date(issue.createdAt);
                const updatedAt = new Date(issue.updatedAt);
                if (createdAt >= weekStart && createdAt <= weekEnd) {
                    created++;
                }
                if (issue.status === 'done' && updatedAt >= weekStart && updatedAt <= weekEnd) {
                    completed++;
                }
                if (issue.status !== 'done' && createdAt <= weekEnd) {
                    pending++;
                }
            });
            weeks.push({ label, completed, pending, created });
        }
        return weeks;
    }, [filteredIssues]);

    // Chart options for monthly trend
    const monthlyChartOptions: ApexOptions = useMemo(() => ({
        chart: { type: 'area', toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent' },
        stroke: { curve: 'smooth', width: 2.5 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 90, 100] } },
        xaxis: { categories: monthlyTrend.map(m => m.label), labels: { style: { fontSize: '10px', colors: '#9CA3AF' } } },
        yaxis: { labels: { style: { fontSize: '10px', colors: '#9CA3AF' } } },
        colors: ['#10B981', '#F59E0B', '#6366F1'],
        legend: { position: 'top', fontSize: '11px', labels: { colors: '#6B7280' } },
        grid: { borderColor: '#F3F4F6', strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { theme: 'light', style: { fontSize: '11px' } },
    }), [monthlyTrend]);

    const monthlyChartSeries = useMemo(() => [
        { name: 'Completed', data: monthlyTrend.map(m => m.completed) },
        { name: 'Pending', data: monthlyTrend.map(m => m.pending) },
        { name: 'Created', data: monthlyTrend.map(m => m.created) },
    ], [monthlyTrend]);

    // Chart options for weekly trend
    const weeklyChartOptions: ApexOptions = useMemo(() => ({
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent', stacked: false },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        xaxis: { categories: weeklyTrend.map(w => w.label), labels: { style: { fontSize: '10px', colors: '#9CA3AF' }, rotate: -45 } },
        yaxis: { labels: { style: { fontSize: '10px', colors: '#9CA3AF' } } },
        colors: ['#10B981', '#EF4444', '#3B82F6'],
        legend: { position: 'top', fontSize: '11px', labels: { colors: '#6B7280' } },
        grid: { borderColor: '#F3F4F6', strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { theme: 'light', style: { fontSize: '11px' } },
    }), [weeklyTrend]);

    const weeklyChartSeries = useMemo(() => [
        { name: 'Completed', data: weeklyTrend.map(w => w.completed) },
        { name: 'Pending', data: weeklyTrend.map(w => w.pending) },
        { name: 'Created', data: weeklyTrend.map(w => w.created) },
    ], [weeklyTrend]);

    // Sorted tasks for Tasks tab
    const sortedTasks = useMemo(() => {
        const sorted = [...filteredIssues].sort((a, b) => {
            let cmp = 0;
            switch (taskSortField) {
                case 'key': cmp = a.key.localeCompare(b.key); break;
                case 'summary': cmp = a.summary.localeCompare(b.summary); break;
                case 'status': cmp = a.status.localeCompare(b.status); break;
                case 'priority': {
                    const order = ['highest', 'high', 'medium', 'low', 'lowest'];
                    cmp = order.indexOf(a.priority) - order.indexOf(b.priority);
                    break;
                }
                case 'dueDate': {
                    const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
                    const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
                    cmp = da - db;
                    break;
                }
            }
            return taskSortDir === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [filteredIssues, taskSortField, taskSortDir]);

    const pagedTasks = useMemo(() => {
        const start = taskPage * TASKS_PER_PAGE;
        return sortedTasks.slice(start, start + TASKS_PER_PAGE);
    }, [sortedTasks, taskPage]);

    const totalPages = Math.ceil(sortedTasks.length / TASKS_PER_PAGE);

    const handleSort = (field: typeof taskSortField) => {
        if (taskSortField === field) {
            setTaskSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setTaskSortField(field);
            setTaskSortDir('asc');
        }
        setTaskPage(0);
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'highest': return 'bg-red-500';
            case 'high': return 'bg-orange-500';
            case 'medium': return 'bg-yellow-500';
            case 'low': return 'bg-green-500';
            case 'lowest': return 'bg-blue-500';
            default: return 'bg-gray-400';
        }
    };

    const getPriorityBadge = (priority: string) => {
        switch (priority) {
            case 'highest': return 'text-red-700 bg-red-50';
            case 'high': return 'text-orange-700 bg-orange-50';
            case 'medium': return 'text-yellow-700 bg-yellow-50';
            case 'low': return 'text-green-700 bg-green-50';
            case 'lowest': return 'text-blue-700 bg-blue-50';
            default: return 'text-gray-700 bg-gray-50';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'done': return 'text-green-700 bg-green-50';
            case 'in-progress': return 'text-blue-700 bg-blue-50';
            case 'in-review': return 'text-purple-700 bg-purple-50';
            case 'to-do': return 'text-gray-700 bg-gray-100';
            case 'backlog': return 'text-gray-500 bg-gray-50';
            case 'on-hold': return 'text-orange-700 bg-orange-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    const getTypeBadge = (type: string) => {
        switch (type) {
            case 'bug': return 'text-red-700 bg-red-50';
            case 'story': return 'text-green-700 bg-green-50';
            case 'task': return 'text-blue-700 bg-blue-50';
            case 'epic': return 'text-purple-700 bg-purple-50';
            default: return 'text-gray-700 bg-gray-50';
        }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const isOverdue = (issue: Issue) => {
        return issue.dueDate && new Date(issue.dueDate) < new Date() && issue.status !== 'done';
    };

    const tabs = [
        { id: 'overview' as const, label: 'Overview', icon: BarChart3 },
        { id: 'users' as const, label: 'Team Members', icon: Users },
        { id: 'projects' as const, label: 'Projects', icon: FolderKanban },
        { id: 'tasks' as const, label: 'All Tasks', icon: Target },
    ];

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-full">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Manager Dashboard</h1>
                    <p className="text-sm text-gray-500 mt-1">Track team performance, project progress, and task completion</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-4 h-4" />
                    Last updated: {new Date().toLocaleTimeString()}
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search tasks..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 text-xs bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                    />
                </div>
                <div className="relative">
                    <select
                        value={selectedProject}
                        onChange={(e) => setSelectedProject(e.target.value)}
                        className="appearance-none px-4 py-2 pr-8 text-xs bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400 cursor-pointer"
                    >
                        <option value="all">All Projects</option>
                        {projects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <div className="relative">
                    <select
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                        className="appearance-none px-4 py-2 pr-8 text-xs bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400 cursor-pointer"
                    >
                        <option value="all">All Members</option>
                        {users.map(u => (
                            <option key={u.id} value={u.id}>{u.displayName}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 bg-white rounded-xl p-1 border border-gray-200 w-fit">
                {tabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${activeTab === tab.id
                                ? 'bg-green-600 text-white shadow-sm'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <Icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Stat Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                        {[
                            { label: 'Total Tasks', value: overallStats.total, icon: Target, color: 'text-blue-600 bg-blue-50' },
                            { label: 'Completed', value: overallStats.done, icon: CheckCircle2, color: 'text-green-600 bg-green-50' },
                            { label: 'In Progress', value: overallStats.inProgress, icon: Clock, color: 'text-indigo-600 bg-indigo-50' },
                            { label: 'To Do', value: overallStats.todo, icon: Target, color: 'text-gray-600 bg-gray-100' },
                            { label: 'In Review', value: overallStats.inReview, icon: Eye, color: 'text-purple-600 bg-purple-50' },
                            { label: 'On Hold', value: overallStats.onHold, icon: Clock, color: 'text-orange-600 bg-orange-50' },
                            { label: 'Overdue', value: overallStats.overdue, icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
                            { label: 'Completion', value: `${overallStats.completionRate}%`, icon: TrendingUp, color: 'text-emerald-600 bg-emerald-50' },
                        ].map((stat, i) => {
                            const Icon = stat.icon;
                            return (
                                <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${stat.color}`}>
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                                    <p className="text-2xs text-gray-500 mt-0.5">{stat.label}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* DEFAULT: All Projects Overview with Team */}
                    {selectedProject === 'all' ? (
                        <>
                            <h3 className="text-sm font-semibold text-gray-900">Projects Overview</h3>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {projectStats.map(({ project, total, done, inProgress, overdue, totalPoints, completedPoints, assigneeCount, completionRate }) => {
                                    const projIssues = filteredIssues.filter(i => i.projectId === project.id);
                                    const projAssigneeIds = new Set<string>();
                                    projIssues.forEach(i => { if (i.assignee) projAssigneeIds.add(i.assignee); i.assignees?.forEach(a => projAssigneeIds.add(a)); });
                                    const projTeam = users.filter(u => projAssigneeIds.has(u.id));
                                    return (
                                        <div key={project.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedProject(project.id)}>
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center text-white font-bold text-sm">{project.avatar || project.key?.charAt(0)}</div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-gray-900 truncate">{project.name}</p>
                                                    <p className="text-2xs text-gray-500">{project.key} • {project.type}</p>
                                                </div>
                                                <div className={`px-2.5 py-1 rounded-lg text-xs font-bold ${completionRate >= 75 ? 'bg-green-100 text-green-700' : completionRate >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{completionRate}%</div>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-2 mb-3"><div className="h-2 rounded-full bg-gradient-to-r from-green-400 to-green-600" style={{ width: `${completionRate}%` }} /></div>
                                            <div className="flex h-1.5 rounded-full overflow-hidden mb-3 bg-gray-100">
                                                {total > 0 && (<><div className="bg-green-500" style={{ width: `${(done / total) * 100}%` }} /><div className="bg-blue-500" style={{ width: `${(inProgress / total) * 100}%` }} /><div className="bg-gray-300" style={{ width: `${((total - done - inProgress) / total) * 100}%` }} /></>)}
                                            </div>
                                            <div className="grid grid-cols-4 gap-2 mb-3">
                                                <div className="text-center"><p className="text-sm font-bold text-gray-900">{total}</p><p className="text-2xs text-gray-500">Total</p></div>
                                                <div className="text-center"><p className="text-sm font-bold text-green-600">{done}</p><p className="text-2xs text-gray-500">Done</p></div>
                                                <div className="text-center"><p className="text-sm font-bold text-blue-600">{inProgress}</p><p className="text-2xs text-gray-500">Active</p></div>
                                                <div className="text-center"><p className="text-sm font-bold text-red-600">{overdue}</p><p className="text-2xs text-gray-500">Overdue</p></div>
                                            </div>
                                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                                <div className="flex items-center gap-1">
                                                    <div className="flex -space-x-2">{projTeam.slice(0, 5).map(u => (<UserAvatar key={u.id} name={u.displayName} src={u.avatarUrl} className="w-6 h-6 ring-2 ring-white" />))}{projTeam.length > 5 && <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-2xs font-bold text-gray-600 ring-2 ring-white">+{projTeam.length - 5}</div>}</div>
                                                    <span className="text-2xs text-gray-500 ml-1">{projTeam.length} members</span>
                                                </div>
                                                <div className="flex items-center gap-1"><Target className="w-3 h-3 text-gray-400" /><span className="text-2xs text-gray-600">{completedPoints}/{totalPoints} pts</span></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        /* SELECTED PROJECT: Detailed Progress View */
                        (() => {
                            const proj = projects.find(p => p.id === selectedProject);
                            const projStat = projectStats.find(ps => ps.project.id === selectedProject);
                            if (!proj || !projStat) return null;
                            const projIssues = filteredIssues;
                            const projAssigneeIds = new Set<string>();
                            projIssues.forEach(i => { if (i.assignee) projAssigneeIds.add(i.assignee); i.assignees?.forEach(a => projAssigneeIds.add(a)); });
                            const projTeam = users.filter(u => projAssigneeIds.has(u.id));
                            const statusCounts: Record<string, number> = { 'To Do': 0, 'In Progress': 0, 'In Review': 0, 'Done': 0, 'Backlog': 0 };
                            projIssues.forEach(i => { if (i.status === 'to-do') statusCounts['To Do']++; else if (i.status === 'in-progress') statusCounts['In Progress']++; else if (i.status === 'in-review') statusCounts['In Review']++; else if (i.status === 'done') statusCounts['Done']++; else if (i.status === 'backlog') statusCounts['Backlog']++; });
                            const sLabels = Object.keys(statusCounts).filter(k => statusCounts[k] > 0);
                            const sValues = sLabels.map(k => statusCounts[k]);
                            const sColors = sLabels.map(k => ({ 'Done': '#10B981', 'In Progress': '#3B82F6', 'In Review': '#8B5CF6', 'To Do': '#6B7280', 'Backlog': '#9CA3AF' }[k] || '#D1D5DB'));
                            const donutOpts: ApexOptions = { chart: { type: 'donut', background: 'transparent' }, labels: sLabels, colors: sColors, legend: { position: 'bottom', fontSize: '11px' }, dataLabels: { enabled: true, style: { fontSize: '11px' } }, plotOptions: { pie: { donut: { size: '55%', labels: { show: true, total: { show: true, label: 'Total', fontSize: '12px', fontWeight: '700' } } } } } };
                            const memberStats = projTeam.map(u => { const mi = projIssues.filter(i => i.assignee === u.id || i.assignees?.includes(u.id)); return { user: u, total: mi.length, done: mi.filter(i => i.status === 'done').length, inProgress: mi.filter(i => i.status === 'in-progress').length, overdue: mi.filter(i => i.dueDate && new Date(i.dueDate) < new Date() && i.status !== 'done').length, rate: mi.length > 0 ? Math.round((mi.filter(i => i.status === 'done').length / mi.length) * 100) : 0 }; }).sort((a, b) => b.total - a.total);
                            const recentIssues = [...projIssues].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 8);
                            return (
                                <>
                                    {/* Project Header */}
                                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center text-white font-bold text-lg">{proj.avatar || proj.key?.charAt(0)}</div>
                                            <div className="flex-1"><h2 className="text-lg font-bold text-gray-900">{proj.name}</h2><p className="text-xs text-gray-500">{proj.key} • {proj.type} • {projTeam.length} members • {projStat.total} tasks</p></div>
                                            <div className="text-right"><p className="text-3xl font-bold text-green-600">{projStat.completionRate}%</p><p className="text-2xs text-gray-500">Complete</p></div>
                                        </div>
                                        <div className="mt-4 w-full bg-gray-100 rounded-full h-3"><div className="h-3 rounded-full bg-gradient-to-r from-green-400 to-green-600" style={{ width: `${projStat.completionRate}%` }} /></div>
                                    </div>
                                    {/* Status Chart + Team */}
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Status Distribution</h3>
                                            {sValues.length > 0 ? <Chart options={donutOpts} series={sValues} type="donut" height={250} /> : <p className="text-xs text-gray-400 text-center py-8">No issues</p>}
                                        </div>
                                        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
                                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Team Members ({projTeam.length})</h3>
                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <thead><tr className="border-b border-gray-100">
                                                        <th className="text-left text-2xs font-semibold text-gray-500 uppercase pb-2">Member</th>
                                                        <th className="text-center text-2xs font-semibold text-gray-500 uppercase pb-2">Tasks</th>
                                                        <th className="text-center text-2xs font-semibold text-gray-500 uppercase pb-2">Done</th>
                                                        <th className="text-center text-2xs font-semibold text-gray-500 uppercase pb-2">Active</th>
                                                        <th className="text-center text-2xs font-semibold text-gray-500 uppercase pb-2">Overdue</th>
                                                        <th className="text-center text-2xs font-semibold text-gray-500 uppercase pb-2">Progress</th>
                                                    </tr></thead>
                                                    <tbody>{memberStats.map(ms => (
                                                        <tr key={ms.user.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                                                            <td className="py-2.5"><div className="flex items-center gap-2"><UserAvatar name={ms.user.displayName} src={ms.user.avatarUrl} className="w-6 h-6" /><span className="text-xs font-medium text-gray-900">{ms.user.displayName}</span></div></td>
                                                            <td className="text-center text-xs font-semibold text-gray-900">{ms.total}</td>
                                                            <td className="text-center text-xs font-semibold text-green-600">{ms.done}</td>
                                                            <td className="text-center text-xs font-semibold text-blue-600">{ms.inProgress}</td>
                                                            <td className="text-center"><span className={`text-xs font-semibold ${ms.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>{ms.overdue}</span></td>
                                                            <td className="text-center"><div className="flex items-center justify-center gap-1.5"><div className="w-14 bg-gray-100 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${ms.rate >= 75 ? 'bg-green-500' : ms.rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${ms.rate}%` }} /></div><span className="text-2xs font-bold text-gray-700">{ms.rate}%</span></div></td>
                                                        </tr>
                                                    ))}</tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Recent Issues */}
                                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Activity</h3>
                                        <div className="space-y-2">
                                            {recentIssues.map(issue => {
                                                const assignee = users.find(u => u.id === (issue.assignee || issue.assignees?.[0]));
                                                return (
                                                    <div key={issue.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => onIssueClick?.(issue)}>
                                                        <span className="text-2xs font-mono font-medium text-green-700 w-16 shrink-0">{issue.key}</span>
                                                        <p className="text-xs text-gray-900 flex-1 truncate">{issue.summary}</p>
                                                        <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold capitalize ${getStatusColor(issue.status)}`}>{issue.status.replace('-', ' ')}</span>
                                                        {assignee && <UserAvatar name={assignee.displayName} src={assignee.avatarUrl} className="w-5 h-5" />}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            );
                        })()
                    )}

                    {/* Priority & Type Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Priority Breakdown</h3>
                            <div className="space-y-3">
                                {Object.entries(priorityBreakdown).map(([priority, count]) => (
                                    <div key={priority} className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full ${getPriorityColor(priority)}`} />
                                        <span className="text-xs font-medium text-gray-700 capitalize w-16">{priority}</span>
                                        <div className="flex-1 bg-gray-100 rounded-full h-2"><div className={`h-2 rounded-full ${getPriorityColor(priority)}`} style={{ width: `${overallStats.total > 0 ? (count / overallStats.total) * 100 : 0}%` }} /></div>
                                        <span className="text-xs font-bold text-gray-900 w-8 text-right">{count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Issue Type Breakdown</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    { type: 'bug', label: 'Bugs', color: 'bg-red-100 text-red-700', emoji: '🐛' },
                                    { type: 'story', label: 'Stories', color: 'bg-green-100 text-green-700', emoji: '📖' },
                                    { type: 'task', label: 'Tasks', color: 'bg-blue-100 text-blue-700', emoji: '✅' },
                                    { type: 'epic', label: 'Epics', color: 'bg-purple-100 text-purple-700', emoji: '🚀' },
                                ].map(item => (
                                    <div key={item.type} className={`rounded-xl p-4 ${item.color}`}>
                                        <div className="flex items-center gap-2 mb-1"><span className="text-lg">{item.emoji}</span><span className="text-xs font-semibold">{item.label}</span></div>
                                        <p className="text-2xl font-bold">{typeBreakdown[item.type] || 0}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Month over Month Trend Chart */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Month over Month Trend</h3>
                                <p className="text-2xs text-gray-500 mt-0.5">Completed vs Pending vs Created issues — last 12 months</p>
                            </div>
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-lg">
                                <TrendingUp className="w-3.5 h-3.5 text-green-600" />
                                <span className="text-2xs font-semibold text-green-700">Monthly</span>
                            </div>
                        </div>
                        <Chart options={monthlyChartOptions} series={monthlyChartSeries} type="area" height={320} />
                    </div>

                    {/* Week over Week Trend Chart */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Week over Week Trend</h3>
                                <p className="text-2xs text-gray-500 mt-0.5">Completed vs Pending vs Created issues — last 12 weeks</p>
                            </div>
                            <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-lg">
                                <BarChart3 className="w-3.5 h-3.5 text-blue-600" />
                                <span className="text-2xs font-semibold text-blue-700">Weekly</span>
                            </div>
                        </div>
                        <Chart options={weeklyChartOptions} series={weeklyChartSeries} type="bar" height={320} />
                    </div>
                </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {userStats.map(({ user, assigned, reported, completed, inProgress, overdue, totalPoints, completedPoints, completionRate }) => (
                        <div key={user.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-3 mb-4">
                                <UserAvatar name={user.displayName} src={user.avatarUrl} className="w-10 h-10" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">{user.displayName}</p>
                                    <p className="text-2xs text-gray-500 truncate">{user.emailAddress || ''}</p>
                                </div>
                                <div className={`px-2 py-1 rounded-lg text-2xs font-bold ${completionRate >= 75 ? 'bg-green-100 text-green-700' :
                                    completionRate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-red-100 text-red-700'
                                    }`}>
                                    {completionRate}%
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
                                <div
                                    className={`h-2 rounded-full transition-all ${completionRate >= 75 ? 'bg-green-500' : completionRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                    style={{ width: `${completionRate}%` }}
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="text-center">
                                    <p className="text-lg font-bold text-gray-900">{assigned}</p>
                                    <p className="text-2xs text-gray-500">Assigned</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-lg font-bold text-green-600">{completed}</p>
                                    <p className="text-2xs text-gray-500">Done</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-lg font-bold text-blue-600">{inProgress}</p>
                                    <p className="text-2xs text-gray-500">Active</p>
                                </div>
                            </div>

                            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-1">
                                    <span className="text-2xs text-gray-500">Reported:</span>
                                    <span className="text-2xs font-semibold text-gray-700">{reported}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-2xs text-gray-500">Points:</span>
                                    <span className="text-2xs font-semibold text-gray-700">{completedPoints}/{totalPoints}</span>
                                </div>
                                {overdue > 0 && (
                                    <div className="flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3 text-red-500" />
                                        <span className="text-2xs font-semibold text-red-600">{overdue} overdue</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {projectStats.map(({ project, total, done, inProgress, overdue, totalPoints, completedPoints, assigneeCount, completionRate }) => (
                        <div
                            key={project.id}
                            className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => onProjectSelect?.(project)}
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                                    {project.avatar || project.key?.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">{project.name}</p>
                                    <p className="text-2xs text-gray-500">{project.key} • {project.type}</p>
                                </div>
                                <div className={`px-2.5 py-1 rounded-lg text-xs font-bold ${completionRate >= 75 ? 'bg-green-100 text-green-700' : completionRate >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                    {completionRate}%
                                </div>
                            </div>

                            <div className="w-full bg-gray-100 rounded-full h-2.5 mb-4">
                                <div className="h-2.5 rounded-full bg-gradient-to-r from-green-400 to-green-600 transition-all" style={{ width: `${completionRate}%` }} />
                            </div>

                            <div className="grid grid-cols-4 gap-3 mb-4">
                                <div className="text-center p-2 bg-gray-50 rounded-lg">
                                    <p className="text-sm font-bold text-gray-900">{total}</p>
                                    <p className="text-2xs text-gray-500">Total</p>
                                </div>
                                <div className="text-center p-2 bg-green-50 rounded-lg">
                                    <p className="text-sm font-bold text-green-600">{done}</p>
                                    <p className="text-2xs text-gray-500">Done</p>
                                </div>
                                <div className="text-center p-2 bg-blue-50 rounded-lg">
                                    <p className="text-sm font-bold text-blue-600">{inProgress}</p>
                                    <p className="text-2xs text-gray-500">Active</p>
                                </div>
                                <div className="text-center p-2 bg-red-50 rounded-lg">
                                    <p className="text-sm font-bold text-red-600">{overdue}</p>
                                    <p className="text-2xs text-gray-500">Overdue</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                <div className="flex items-center gap-1">
                                    <Users className="w-3.5 h-3.5 text-gray-400" />
                                    <span className="text-2xs text-gray-600">{assigneeCount} members</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Target className="w-3.5 h-3.5 text-gray-400" />
                                    <span className="text-2xs text-gray-600">{completedPoints}/{totalPoints} points</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left text-2xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 cursor-pointer hover:text-gray-700" onClick={() => handleSort('key')}>
                                        Key {taskSortField === 'key' && (taskSortDir === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="text-left text-2xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 cursor-pointer hover:text-gray-700" onClick={() => handleSort('summary')}>
                                        Summary {taskSortField === 'summary' && (taskSortDir === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="text-left text-2xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Project</th>
                                    <th className="text-left text-2xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 cursor-pointer hover:text-gray-700" onClick={() => handleSort('status')}>
                                        Status {taskSortField === 'status' && (taskSortDir === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="text-left text-2xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 cursor-pointer hover:text-gray-700" onClick={() => handleSort('priority')}>
                                        Priority {taskSortField === 'priority' && (taskSortDir === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="text-left text-2xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Type</th>
                                    <th className="text-left text-2xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Assignee</th>
                                    <th className="text-left text-2xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 cursor-pointer hover:text-gray-700" onClick={() => handleSort('dueDate')}>
                                        Due Date {taskSortField === 'dueDate' && (taskSortDir === 'asc' ? '↑' : '↓')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedTasks.map(issue => {
                                    const proj = projects.find(p => p.id === issue.projectId);
                                    const assigneeId = issue.assignee || issue.assignees?.[0];
                                    const assignee = assigneeId ? users.find(u => u.id === assigneeId) : null;
                                    return (
                                        <tr
                                            key={issue.id}
                                            className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer transition-colors"
                                            onClick={() => onIssueClick?.(issue)}
                                        >
                                            <td className="px-4 py-3">
                                                <span className="text-xs font-mono font-medium text-green-700">{issue.key}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="text-xs text-gray-900 line-clamp-1 max-w-xs">{issue.summary}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-2xs text-gray-600">{proj?.key || '—'}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-block px-2 py-0.5 rounded-full text-2xs font-semibold capitalize ${getStatusColor(issue.status)}`}>
                                                    {issue.status.replace('-', ' ')}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-block px-2 py-0.5 rounded-full text-2xs font-semibold capitalize ${getPriorityBadge(issue.priority)}`}>
                                                    {issue.priority}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-block px-2 py-0.5 rounded-full text-2xs font-semibold capitalize ${getTypeBadge(issue.type)}`}>
                                                    {issue.type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {assignee ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <UserAvatar name={assignee.displayName} src={assignee.avatarUrl} className="w-5 h-5" />
                                                        <span className="text-2xs text-gray-700">{assignee.displayName}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-2xs text-gray-400">Unassigned</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-2xs ${isOverdue(issue) ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                                    {formatDate(issue.dueDate)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {pagedTasks.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-12 text-center">
                                            <p className="text-sm text-gray-400">No tasks found matching your filters</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                            <p className="text-2xs text-gray-500">
                                Showing {taskPage * TASKS_PER_PAGE + 1}–{Math.min((taskPage + 1) * TASKS_PER_PAGE, sortedTasks.length)} of {sortedTasks.length} tasks
                            </p>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setTaskPage(p => Math.max(0, p - 1))}
                                    disabled={taskPage === 0}
                                    className="px-3 py-1 text-2xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                    const page = taskPage < 3 ? i : taskPage - 2 + i;
                                    if (page >= totalPages) return null;
                                    return (
                                        <button
                                            key={page}
                                            onClick={() => setTaskPage(page)}
                                            className={`w-7 h-7 text-2xs font-medium rounded-lg ${page === taskPage ? 'bg-green-600 text-white' : 'border border-gray-200 bg-white hover:bg-gray-50 text-gray-700'}`}
                                        >
                                            {page + 1}
                                        </button>
                                    );
                                })}
                                <button
                                    onClick={() => setTaskPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={taskPage >= totalPages - 1}
                                    className="px-3 py-1 text-2xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};